import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart, type CartItem } from '@/hooks/useCart';
import { cartLineKey, getCartLineDisplayName, getCartLineUnitPrice } from '@/utils/cartLine';
import { productService } from '@/services/product.service';
import { Product } from '@/types/product.types';
import { formatPrice } from '@/utils/formatters';
import { XMarkIcon, TrashIcon, PlusIcon, MinusIcon, ShoppingBagIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CartSidebar = ({ isOpen, onClose }: CartSidebarProps) => {
  const { cart, removeFromCart, updateQuantity, cartTotal } = useCart();
  const [products, setProducts] = useState<Map<number, Product>>(new Map());
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      if (cart.length > 0) {
        loadProducts();
      } else {
        // Si le panier est vide, s'assurer que loading est false
        setLoading(false);
        setProducts(new Map());
      }
    }
  }, [isOpen, cart]);

  const loadProducts = async () => {
    if (cart.length === 0) {
      setLoading(false);
      setProducts(new Map());
      return;
    }

    setLoading(true);
    try {
      const productMap = new Map<number, Product>();
      const validItems: typeof cart = [];
      
      for (const item of cart) {
        try {
          const product = await productService.getById(item.id);
          if (product) {
            productMap.set(item.id, product);
            validItems.push(item);
          }
        } catch (error) {
          console.error(`Erreur lors du chargement du produit ${item.id}:`, error);
          // Ne pas ajouter le produit à validItems si erreur
        }
      }
      
      setProducts(productMap);
      
      // Si certains produits n'ont pas pu être chargés, nettoyer le panier
      if (validItems.length !== cart.length) {
        console.warn(`${cart.length - validItems.length} produit(s) non trouvé(s), nettoyage du panier`);
        // Optionnel: nettoyer automatiquement les produits invalides
        // const { clearCart } = useCart(); // Ne peut pas être utilisé ici
        // On laisse l'utilisateur voir les produits valides
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = (item: CartItem) => {
    removeFromCart(item.id, item.selected_pack_id);
  };

  const handleIncreaseQuantity = (item: CartItem) => {
    updateQuantity(item.id, item.quantity + 1, item.selected_pack_id);
  };

  const handleDecreaseQuantity = (item: CartItem) => {
    if (item.quantity > 1) {
      updateQuantity(item.id, item.quantity - 1, item.selected_pack_id);
    } else {
      handleRemoveItem(item);
    }
  };

  const handleCheckout = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (cart.length === 0) {
      console.warn('Le panier est vide, impossible de commander');
      alert('Votre panier est vide');
      return;
    }
    
    console.log('Redirection vers /checkout depuis le panier...', { 
      cartLength: cart.length,
      cartTotal,
      cartItems: cart.map(item => ({ id: item.id, quantity: item.quantity }))
    });
    
    // Fermer le panier d'abord
    onClose();
    
    // Utiliser requestAnimationFrame puis setTimeout pour s'assurer que la navigation se fait après la fermeture
    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          console.log('Tentative de navigation vers /checkout...');
          navigate('/checkout', { replace: false });
          console.log('Navigation réussie vers /checkout');
        } catch (error) {
          console.error('Erreur lors de la navigation avec navigate:', error);
          // Alternative: utiliser window.location si navigate ne fonctionne pas
          console.log('Tentative avec window.location.href...');
          window.location.href = '/checkout';
        }
      }, 100);
    });
  };

  const handleViewDetails = (productId: number) => {
    onClose();
    navigate(`/produit-detail?id=${productId}`);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="cart-sidebar-overlay" onClick={onClose}></div>

      {/* Sidebar */}
      <div 
        className="cart-sidebar" 
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="cart-sidebar-header">
          <div className="cart-sidebar-title">
            <ShoppingBagIcon className="w-6 h-6" />
            <h2>Mon Panier</h2>
            {cart.length > 0 && (
              <span className="cart-items-count">({cart.length} {cart.length === 1 ? 'article' : 'articles'})</span>
            )}
          </div>
          <button 
            className="cart-sidebar-close" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Fermer le panier"
            type="button"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="cart-sidebar-content">
          {loading ? (
            <div className="cart-loading">
              <div>Chargement des produits...</div>
            </div>
          ) : cart.length === 0 ? (
            <div className="cart-empty">
              <ShoppingBagIcon className="w-16 h-16" style={{ color: '#ccc', marginBottom: '16px' }} />
              <p>Votre panier est vide</p>
            </div>
          ) : (
            <>
              <div className="cart-items-list">
                {cart.map((item) => {
                  const product = products.get(item.id);
                  if (!product) {
                    // Si le produit n'est pas chargé et qu'on n'est plus en loading, c'est qu'il n'existe plus
                    if (!loading) {
                      return (
                        <div key={cartLineKey(item)} className="cart-item-error" style={{ 
                          padding: '16px', 
                          border: '1px solid #ff6b6b', 
                          borderRadius: '8px', 
                          marginBottom: '12px',
                          backgroundColor: '#fff5f5'
                        }}>
                          <p style={{ color: '#ff6b6b', margin: 0, marginBottom: '8px' }}>
                            Produit introuvable (ID: {item.id})
                          </p>
                          <button
                            onClick={() => handleRemoveItem(item)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#ff6b6b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            Retirer du panier
                          </button>
                        </div>
                      );
                    }
                    // Sinon, afficher le chargement
                    return (
                      <div key={cartLineKey(item)} className="cart-item-loading">
                        Chargement...
                      </div>
                    );
                  }

                  const unitPrice = getCartLineUnitPrice(item, product);
                  const itemTotal = unitPrice * item.quantity;
                  const lineTitle = getCartLineDisplayName(item, product) || product.name;

                  return (
                    <div key={cartLineKey(item)} className="cart-item">
                      <div className="cart-item-image">
                        <img
                          src={product.image_url || '/images/bottle.png'}
                          alt={lineTitle}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/images/bottle.png';
                          }}
                        />
                      </div>
                      <div className="cart-item-details">
                        <h3 className="cart-item-name">{lineTitle}</h3>
                        <div className="cart-item-price">
                          {formatPrice(unitPrice)} / unité
                        </div>
                        <div className="cart-item-actions">
                          <div className="quantity-controls-small">
                            <button
                              className="quantity-btn-small"
                              onClick={() => handleDecreaseQuantity(item)}
                              aria-label="Diminuer la quantité"
                            >
                              <MinusIcon className="w-4 h-4" />
                            </button>
                            <span className="quantity-value">{item.quantity}</span>
                            <button
                              className="quantity-btn-small"
                              onClick={() => handleIncreaseQuantity(item)}
                              aria-label="Augmenter la quantité"
                            >
                              <PlusIcon className="w-4 h-4" />
                            </button>
                          </div>
                          <button
                            className="cart-item-remove"
                            onClick={() => handleRemoveItem(item)}
                            aria-label="Retirer du panier"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="cart-item-total">
                          Total: <strong>{formatPrice(itemTotal)}</strong>
                        </div>
                        <button
                          className="btn-view-details"
                          onClick={() => handleViewDetails(item.id)}
                        >
                          Voir les détails <ArrowRightIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary */}
              <div className="cart-summary">
                <div className="cart-summary-row">
                  <span>Sous-total</span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
                <div className="cart-summary-row">
                  <span>Livraison</span>
                  <span>Gratuite</span>
                </div>
                <div className="cart-summary-total">
                  <span>Total</span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="cart-actions">
                <button 
                  type="button"
                  className="btn-checkout" 
                  onClick={handleCheckout}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  disabled={cart.length === 0}
                  style={{ pointerEvents: cart.length === 0 ? 'none' : 'auto' }}
                >
                  Commander maintenant
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

